import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import MemberLayout from './components/MemberLayout';
import MemberDashboard from './pages/member/MemberDashboard';
import SubmitPayment from './pages/member/SubmitPayment';
import MyPayments from './pages/member/MyPayments';
import MyProfile from './pages/member/MyProfile';
import MySubscriptions from './pages/member/MySubscriptions';
import MyMembers from './pages/member/MyMembers';
import ScanFollowup from './pages/member/ScanFollowup';
import MarkAttendance from './pages/member/MarkAttendance';
import PendingApprovals from './pages/finance/PendingApprovals';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import AllMembers from './pages/members/AllMembers';
import AddMember from './pages/members/AddMember';
import MemberProfile from './pages/members/MemberProfile';
import EditMember from './pages/members/EditMember';
import MembershipStatus from './pages/members/MembershipStatus';
import RecordAttendance from './pages/attendance/RecordAttendance';
import AttendanceHistory from './pages/attendance/AttendanceHistory';
import AbsenteeReport from './pages/attendance/AbsenteeReport';
import SpiritualFathers from './pages/spiritual/SpiritualFathers';
import AllFamilies from './pages/spiritual/AllFamilies';
import AddFamily from './pages/spiritual/AddFamily';
import FollowupRecords from './pages/spiritual/FollowupRecords';
import Assignment from './pages/spiritual/Assignment';
import CR from './pages/finance/CR';
import CRView from './pages/finance/CRView';
import PV from './pages/finance/PV';
import AP from './pages/finance/AP';
import CashBook from './pages/finance/CashBook';
import GRN from './pages/stock/GRN';
import SIV from './pages/stock/SIV';
import AssetRegister from './pages/stock/AssetRegister';
import SpiritualFatherReport from './pages/reports/SpiritualFatherReport';
import FinancialSummary from './pages/reports/FinancialSummary';
import SubscriptionStatus from './pages/reports/SubscriptionStatus';
import ChurchProfile from './pages/settings/ChurchProfile';
import UsersRoles from './pages/settings/UsersRoles';
import Permissions from './pages/settings/Permissions';
import ResetData from './pages/settings/ResetData';
import AuditLog from './pages/settings/AuditLog';
import FundCategories from './pages/settings/FundCategories';
import OpeningBalance from './pages/settings/OpeningBalance';
import PhoneEntry from './pages/PhoneEntry';
import PublicVerify from './pages/PublicVerify';
import Departments from './pages/settings/Departments';
import InfoHub from './pages/info/InfoHub';
import InfoPage from './pages/info/InfoPage';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontSize:32 }}>✝️</div>;
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontSize:32 }}>✝️</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role === 'member') return <Navigate to="/member/dashboard" />;
  return children;
};

const MemberRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontSize:32 }}>✝️</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  // Members always pass. Any staff user (admin, cashier, finance, secretary,
  // pastor, cell_leader) is allowed in too if they have a linked member
  // record — that's what the "Switch to Member Portal" button uses.
  if (user?.role === 'member') return children;
  if (user?.member_id) return children;
  return <Navigate to="/dashboard" />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      {/* v1.9.8 — public CR verification page (no login). Reached by QR scan
          from a printed receipt. Backend endpoint is also unauthenticated. */}
      <Route path="/verify/:token" element={<PublicVerify />} />

      {/* Standalone temp helper — passcode-gated, no Layout, no sidebar.
          Only the phone/whatsapp columns on members are reachable. */}
      <Route path="/phone-entry" element={<PhoneEntry />} />

      {/* Member Portal */}
      <Route path="/member" element={<MemberRoute><MemberLayout /></MemberRoute>}>
        <Route index element={<Navigate to="/member/dashboard" />} />
        <Route path="dashboard" element={<MemberDashboard />} />
        <Route path="submit-payment" element={<SubmitPayment />} />
        <Route path="my-payments" element={<MyPayments />} />
        <Route path="profile" element={<MyProfile />} />
        <Route path="subscriptions" element={<MySubscriptions />} />
        <Route path="my-members" element={<MyMembers />} />
        <Route path="scan-followup" element={<ScanFollowup />} />
        <Route path="mark-attendance" element={<MarkAttendance />} />
        <Route path="info" element={<InfoHub basePath="/member/info" />} />
        <Route path="info/:slug" element={<InfoPage basePath="/member/info" />} />
        <Route path="member-profile/:id" element={<MemberProfile />} />
      </Route>

      <Route path="/" element={<AdminRoute><Layout /></AdminRoute>}>
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="members" element={<AllMembers />} />
        <Route path="members/former" element={<AllMembers />} />
        <Route path="members/add" element={<AddMember />} />
        <Route path="members/profile/:id" element={<MemberProfile />} />
        <Route path="members/:id/edit" element={<EditMember />} />
        <Route path="members/status" element={<MembershipStatus />} />
        <Route path="attendance/record" element={<RecordAttendance />} />
        <Route path="attendance/history" element={<AttendanceHistory />} />
        <Route path="attendance/absentees" element={<AbsenteeReport />} />
        <Route path="spiritual/fathers" element={<SpiritualFathers />} />
        <Route path="spiritual/families" element={<AllFamilies />} />
        <Route path="spiritual/add-family" element={<AddFamily />} />
        <Route path="spiritual/followups" element={<FollowupRecords />} />
        <Route path="spiritual/assignment" element={<Assignment />} />
        <Route path="finance/cr" element={<CR />} />
        <Route path="finance/cr/:crNumber" element={<CRView />} />
        <Route path="finance/approvals" element={<PendingApprovals />} />
        <Route path="finance/pv" element={<PV />} />
        <Route path="finance/ap" element={<AP />} />
        <Route path="finance/cashbook" element={<CashBook />} />
        <Route path="stock/grn" element={<GRN />} />
        <Route path="stock/siv" element={<SIV />} />
        <Route path="stock/assets" element={<AssetRegister />} />
        <Route path="reports/spiritual-father" element={<SpiritualFatherReport />} />
        <Route path="reports/financial" element={<FinancialSummary />} />
        <Route path="reports/subscription-status" element={<SubscriptionStatus />} />
        <Route path="settings/profile" element={<ChurchProfile />} />
        <Route path="settings/users" element={<UsersRoles />} />
        <Route path="settings/permissions" element={<Permissions />} />
        <Route path="settings/reset" element={<ResetData />} />
        <Route path="settings/audit" element={<AuditLog />} />
        <Route path="settings/funds" element={<FundCategories />} />
        <Route path="settings/opening-balance" element={<OpeningBalance />} />
        <Route path="settings/departments" element={<Departments />} />
        <Route path="info" element={<InfoHub basePath="/info" />} />
        <Route path="info/:slug" element={<InfoPage basePath="/info" />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
